/**
 * useDragAutoScroll — scrolla automaticamente la pagina durante un drag
 * quando il cursore si avvicina al bordo top/bottom della viewport.
 *
 * Andrea 2026-05-20: su schermi piccoli (laptop, mobile landscape) il
 * drag-and-drop HTML5 blocca lo scroll del browser → impossibile
 * raggiungere gli slot in basso (attaccanti) o in alto (panchina sopra).
 * Listener globale `dragover` sul document: quando il puntatore è entro
 * `THRESHOLD_PX` dal bordo, fa `window.scrollBy` proporzionale.
 *
 * Lo scroll automatico è disattivato se nessun drag è in corso.
 */

import { useEffect } from "react";

const THRESHOLD_PX = 120; // pixel dal bordo per attivare lo scroll
const MAX_STEP_PX = 18; // velocità massima di scroll per frame

export function useDragAutoScroll(): void {
  useEffect(() => {
    let dragging = false;
    let rafId: number | null = null;
    let lastY = 0;

    const tick = (): void => {
      if (!dragging) {
        rafId = null;
        return;
      }
      const vh = window.innerHeight;
      const yFromTop = lastY;
      const yFromBottom = vh - lastY;
      let dy = 0;
      if (yFromTop < THRESHOLD_PX) {
        // più vicino al bordo → scroll più veloce (proporzionale)
        const ratio = 1 - yFromTop / THRESHOLD_PX;
        dy = -Math.ceil(ratio * MAX_STEP_PX);
      } else if (yFromBottom < THRESHOLD_PX) {
        const ratio = 1 - yFromBottom / THRESHOLD_PX;
        dy = Math.ceil(ratio * MAX_STEP_PX);
      }
      if (dy !== 0) {
        window.scrollBy({ top: dy, behavior: "auto" });
      }
      rafId = window.requestAnimationFrame(tick);
    };

    const onDragStart = (): void => {
      dragging = true;
      if (rafId === null) {
        rafId = window.requestAnimationFrame(tick);
      }
    };
    const onDragOver = (e: DragEvent): void => {
      // clientY è la posizione attuale del cursore durante il drag
      lastY = e.clientY;
      dragging = true;
      if (rafId === null) {
        rafId = window.requestAnimationFrame(tick);
      }
    };
    const stop = (): void => {
      dragging = false;
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragend", stop);
    document.addEventListener("drop", stop);
    document.addEventListener("dragexit", stop);
    return () => {
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragend", stop);
      document.removeEventListener("drop", stop);
      document.removeEventListener("dragexit", stop);
      stop();
    };
  }, []);
}
